import { useSyncExternalStore } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { TARS_NOTEBOOK_LAST_TOUCHED_STORAGE_KEY } from "../tars-constants";

type LastTouchedMap = Record<string, number>;

const TARS_NOTEBOOKS_LIST_QUERY_KEY = ["notebooks"] as const;

const tarsNotebookLastTouchedKey = (notebookId: number) =>
  ["tars", "notebook", "lastTouched", notebookId] as const;

function loadLastTouchedMap(): LastTouchedMap {
  try {
    const raw = localStorage.getItem(TARS_NOTEBOOK_LAST_TOUCHED_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as LastTouchedMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveLastTouchedMap(map: LastTouchedMap) {
  try {
    localStorage.setItem(TARS_NOTEBOOK_LAST_TOUCHED_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

function readStoredLastTouched(notebookId: number): number | undefined {
  const ms = loadLastTouchedMap()[String(notebookId)];
  return typeof ms === "number" && Number.isFinite(ms) ? ms : undefined;
}

export function readTarsNotebookLastTouched(
  queryClient: QueryClient,
  notebookId: number
): number | undefined {
  const fromCache = queryClient.getQueryData<number>(tarsNotebookLastTouchedKey(notebookId));
  const fromStorage = readStoredLastTouched(notebookId);
  if (fromCache == null) return fromStorage;
  if (fromStorage == null) return fromCache;
  return Math.max(fromCache, fromStorage);
}

export function invalidateTarsNotebooksList(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: TARS_NOTEBOOKS_LIST_QUERY_KEY });
}

export function markTarsNotebookActivity(
  queryClient: QueryClient,
  notebookId: number | null | undefined
) {
  if (notebookId == null || !Number.isFinite(notebookId) || notebookId <= 0) return;
  const ms = Date.now();
  const map = loadLastTouchedMap();
  map[String(notebookId)] = ms;
  saveLastTouchedMap(map);
  queryClient.setQueryData(tarsNotebookLastTouchedKey(notebookId), ms);
  invalidateTarsNotebooksList(queryClient);
}

function buildLastTouchedSnapshot(
  queryClient: QueryClient,
  notebookIds: number[]
): string {
  const parts: string[] = [];
  for (const id of notebookIds) {
    const ms = readTarsNotebookLastTouched(queryClient, id);
    if (ms != null) parts.push(`${id}:${ms}`);
  }
  return parts.join("|");
}

function snapshotToLastTouchedMap(snapshot: string): Map<number, number> {
  const map = new Map<number, number>();
  if (!snapshot) return map;
  for (const part of snapshot.split("|")) {
    const [idStr, msStr] = part.split(":");
    const id = Number(idStr);
    const ms = Number(msStr);
    if (Number.isFinite(id) && Number.isFinite(ms)) map.set(id, ms);
  }
  return map;
}

export function useTarsNotebookLastTouchedMap(notebookIds: number[]): Map<number, number> {
  const queryClient = useQueryClient();
  const snapshot = useSyncExternalStore(
    (onStoreChange) => queryClient.getQueryCache().subscribe(onStoreChange),
    () => buildLastTouchedSnapshot(queryClient, notebookIds),
    () => ""
  );
  return snapshotToLastTouchedMap(snapshot);
}
